import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal, Tabs, message } from "antd";
import { CheckCircleFilled, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useIntl } from "react-intl";
import { API } from "../apiUrl";
import { getToken } from "./AuthPanel";
import { PLAN_DAYS } from "../services/plan";
import type { ThemeName } from "./BottomToolbar";

interface Props {
  open: boolean;
  theme: ThemeName;
  onClose: () => void;
  onSelectDate: (dateKey: string) => void;
}

interface MyShareReply {
  id: string;
  author: string;
  text: string;
  at: number;
}

interface MyShareItem {
  id: string;
  dateKey: string;
  text: string;
  at: number;
  replies: MyShareReply[];
}

const PAGE_SIZE = 20;

const themeVars = (theme: ThemeName): React.CSSProperties => {
  if (theme === "dark") {
    return { "--ink": "#eee", "--muted": "#999", "--accent": "#ff6b6b", "--card-bg": "#1f1f1f", "--border": "#2a2a2a" } as React.CSSProperties;
  }
  if (theme === "light") {
    return { "--ink": "#333", "--muted": "#999", "--accent": "#e63946", "--card-bg": "#fff", "--border": "#f0f0f0" } as React.CSSProperties;
  }
  return { "--ink": "#333", "--muted": "#888", "--accent": "#8b4513", "--card-bg": "#fff", "--border": "#eee7db" } as React.CSSProperties;
};

const MyCheckins: React.FC<Props> = ({ open, theme, onClose, onSelectDate }) => {
  const intl = useIntl();
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [doneSet, setDoneSet] = useState<Set<string>>(new Set());
  const [tab, setTab] = useState("checkins");
  const [myShares, setMyShares] = useState<MyShareItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState("");
  const [sharesLoading, setSharesLoading] = useState(false);

  const planDates = useMemo(() => new Set(PLAN_DAYS.map((p) => p.date)), []);
  const todayYmd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const weekLabels = useMemo(
    () => Array.from({ length: 7 }, (_, i) => intl.formatDate(new Date(2026, 0, 4 + i), { weekday: "short" })),
    [intl]
  );

  const load = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/checkins/mine`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const data = (await res.json()) as { dates?: string[] };
      setDoneSet(new Set(data.dates || []));
    } catch {}
  }, []);

  const loadShares = useCallback(
    async (reset: boolean, cursor?: string) => {
      const token = getToken();
      if (!token) return;
      setSharesLoading(true);
      try {
        const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
        if (cursor) params.set("cursor", cursor);
        const res = await fetch(`${API}/api/shares/mine?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = (await res.json()) as { items?: MyShareItem[]; nextCursor?: string; hasMore?: boolean };
        setMyShares((prev) => (reset ? data.items || [] : [...prev, ...(data.items || [])]));
        setNextCursor(data.nextCursor || "");
        setHasMore(Boolean(data.hasMore));
      } catch {
      } finally {
        setSharesLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    if (open && tab === "shares") loadShares(true);
  }, [open, tab, loadShares]);

  const catchUp = useCallback(
    async (ymd: string) => {
      const token = getToken();
      if (!token) {
        message.info(intl.formatMessage({ id: "auth.benefits" }));
        return;
      }
      try {
        const res = await fetch(`${API}/api/checkin`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ dateKey: ymd }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        message.success(intl.formatMessage({ id: "reading.checkinDone" }));
        setDoneSet((prev) => new Set(prev).add(ymd));
      } catch {
        message.error(intl.formatMessage({ id: "common.error" }));
      }
    },
    [intl]
  );

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstWeekday = new Date(viewYear, viewMonth, 1).getDay();
  const cells: (number | null)[] = [
    ...Array.from({ length: firstWeekday }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewYear(viewYear - 1);
      setViewMonth(11);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };
  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewYear(viewYear + 1);
      setViewMonth(0);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const calendar = (
    <div className="my-checkins" style={themeVars(theme)}>
      <div className="my-checkins-head">
        <Button size="small" icon={<LeftOutlined />} onClick={prevMonth} />
        <span className="my-checkins-month">
          {intl.formatDate(new Date(viewYear, viewMonth, 1), { year: "numeric", month: "long" })}
        </span>
        <Button size="small" icon={<RightOutlined />} onClick={nextMonth} />
      </div>
      <div className="my-checkins-week">
        {weekLabels.map((w, i) => (
          <span key={i} className="my-checkins-weekday">
            {w}
          </span>
        ))}
      </div>
      <div className="my-checkins-grid">
        {cells.map((d, i) => {
          if (d === null) return <span key={`e${i}`} className="my-checkins-cell empty" />;
          const dateKeyM = `${viewMonth + 1}.${d}`;
          const ymd = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          if (!planDates.has(dateKeyM)) {
            return <span key={`o${i}`} className="my-checkins-cell empty" />;
          }
          const done = doneSet.has(ymd);
          const isToday = ymd === todayYmd;
          const canCatchUp = !done && !isToday && ymd < todayYmd;
          return (
            <div
              key={i}
              className={`my-checkins-cell${done ? " done" : ""}${isToday ? " today" : ""}${isToday && !done ? " missed" : ""}`}
            >
              <button type="button" className="my-checkins-day" onClick={() => onSelectDate(dateKeyM)}>
                {d}
              </button>
              {done && <CheckCircleFilled className="my-checkins-done-mark" />}
              {isToday && !done && <span className="my-checkins-missed-dot" />}
              {canCatchUp && (
                <button type="button" className="my-checkins-catchup" onClick={() => catchUp(ymd)}>
                  {intl.formatMessage({ id: "myCheckins.catchUp" })}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const sharesList = (
    <div className="my-shares" style={themeVars(theme)}>
      {myShares.length === 0 ? (
        <div className="my-shares-empty">{intl.formatMessage({ id: "reading.noShares" })}</div>
      ) : (
        myShares.map((s) => (
          <div key={s.id} className="share-item">
            <div className="share-meta">
              <span>
                {s.dateKey} · {intl.formatDate(new Date(s.at), { dateStyle: "medium", timeStyle: "short" })}
              </span>
            </div>
            <div className="share-text">{s.text}</div>
            {s.replies.length > 0 && (
              <div className="my-share-replies">
                {s.replies.map((r) => (
                  <div key={r.id} className="my-share-reply">
                    <div className="my-share-reply-meta">
                      <span className="my-share-reply-tag">{intl.formatMessage({ id: "reading.replyQuote" })}</span>
                      {r.author} · {intl.formatDate(new Date(r.at), { dateStyle: "medium", timeStyle: "short" })}
                    </div>
                    <div className="my-share-reply-text">{r.text}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))
      )}
      {hasMore && (
        <div className="my-shares-load-more">
          <Button size="small" loading={sharesLoading} onClick={() => loadShares(false, nextCursor)}>
            {intl.formatMessage({ id: "myCheckins.loadMore" })}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={intl.formatMessage({ id: "myCheckins.title" })}
      width={400}
      className="my-checkins-modal"
    >
      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          { key: "checkins", label: intl.formatMessage({ id: "myCheckins.title" }), children: calendar },
          { key: "shares", label: intl.formatMessage({ id: "myCheckins.sharesTab" }), children: sharesList },
        ]}
      />
    </Modal>
  );
};

export default MyCheckins;
